import { useTranslation } from 'react-i18next'

import { Insights, useFilters, useInsightsMetric } from '@percent/domain/insights'
import { Badge, Heading, Icon, Text } from '@percent/lemonade'
import { dayJS } from '@percent/partner-dashboard/common/library/utility/date'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import { DONATION_COLOUR_MAP } from './insightsColourMap'
import styles from './DonationInsights.module.scss'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const tDynamic = (t: (key: never, options?: any) => string, key: string, options?: Record<string, unknown>) =>
  t(key as never, options)

// Date-range preset tokens with dedicated trend-comparison copy. Mirrors the
// backend's preset keys; each maps to `typography.dashboard.trend.<key>`.
const TREND_PRESET_KEYS = new Set(['last7days', 'last30days', 'last90days', 'last12months', 'ytd', 'lastyear'])

const daysBetween = (from: string, to: string): number =>
  Math.max(1, dayJS(to).utc().startOf('day').diff(dayJS(from).utc().startOf('day'), 'day') + 1)

/**
 * The localised "Compared to …" comparison-window copy for the active date range —
 * product UX that depends on the selected preset/range, so it lives in the app and
 * is handed to the hero card as its trend tooltip. A preset maps to its dedicated
 * key, a custom range to the day-count phrasing, and all-time to no tooltip. The
 * lib appends the prior-period value (when the trend carries one).
 */
function useTrendTooltip(): string | undefined {
  const { t } = useTranslation()
  const { values } = useFilters()
  // The hero card already fetches the summary metric; react-query dedupes this by
  // query key, so it's the same request — we read its structured trend to surface
  // the backend's actual comparison boundary in the copy.
  const { data: summary } = useInsightsMetric('summary', { filters: values })
  const period = typeof values.period === 'string' ? values.period : undefined
  const from = typeof values.from === 'string' ? values.from : undefined
  const to = typeof values.to === 'string' ? values.to : undefined

  // Year-to-date compares against the same point in the prior year, so spell out
  // that end date ("…up to 2 Jun 2025"). Prefer the trend's own `period.to` (the
  // backend's actual boundary); fall back to today-last-year before it loads.
  if (period === 'ytd') {
    const comparisonEnd = summary?.data.type === 'scalar' ? summary.data.trend?.period.to : undefined
    const date = comparisonEnd
      ? dayJS(comparisonEnd).utc().format('D MMM YYYY')
      : dayJS().subtract(1, 'year').format('D MMM YYYY')

    return tDynamic(t, 'typography.dashboard.trend.ytd', { date })
  }

  if (period && TREND_PRESET_KEYS.has(period)) return tDynamic(t, `typography.dashboard.trend.${period}`)

  if (from && to) return tDynamic(t, 'typography.dashboard.trend.previousNDays', { count: daysBetween(from, to) })

  return undefined
}

// Rendered inside `Insights.Domain` so it can read the active filter values (for
// the trend-comparison copy) via the domain's filter adapter.
function DonationSummaryCharts() {
  const { t } = useTranslation()
  const trendTooltip = useTrendTooltip()

  const emptyState = (
    <>
      <Icon name="empty" size={6} color="goodstack" />
      <Text size="small">{t('typography.dashboard.noDataToShow')}</Text>
    </>
  )
  const errorLabel = t('typography.dashboard.failedToLoad')

  return (
    <div className={styles.chartsContainer} data-testid="donation-summary-charts">
      <Insights.Section title={t('typography.dashboard.donationSummary')}>
        {/* Hero number (summary) above the cause-area breakdown bars, one card. */}
        <Insights.BarChart
          heroMetric="summary"
          metric="by-cause-area"
          limit={5}
          wrapXAxisLabels
          trendTooltip={trendTooltip}
          emptyState={emptyState}
          errorLabel={errorLabel}
          testId="donation-summary-charts-by-cause-area"
        />
        <Insights.BarChart
          metric="by-organisation"
          limit={5}
          orientation="horizontal"
          yAxisWidth={140}
          emptyState={emptyState}
          errorLabel={errorLabel}
          testId="donation-summary-charts-by-organisation"
        />
        <Insights.DonutChart
          metric="by-region"
          emptyState={emptyState}
          errorLabel={errorLabel}
          testId="donation-summary-charts-by-region"
        />
      </Insights.Section>
    </div>
  )
}

export function DonationInsights() {
  const { t } = useTranslation()
  const { insightsService } = useServices()

  // All filter UI + state (date range presets, cause-area / region / category
  // multi-selects) is owned by the generic `Insights.Filters`; the cards read the
  // active values from the domain's filter adapter, so no `filters` prop.
  const filterLabels = {
    reset: t('typography.dashboard.filters.reset'),
    all: t('typography.dashboard.filters.all'),
    optionsError: t('typography.dashboard.filters.optionsLoadError'),
    dateRange: {
      label: t('typography.dashboard.filters.dateRange'),
      allTime: t('typography.dashboard.filters.allTime'),
      custom: t('typography.dashboard.filters.preset.custom'),
      apply: t('typography.dashboard.filters.apply'),
      clear: t('typography.dashboard.filters.clear')
    }
  }

  return (
    <Insights.Domain domain="donations" service={insightsService} colourMap={DONATION_COLOUR_MAP}>
      <div className={styles.header} data-testid="donation-insights-header">
        <div className={styles.titleGroup}>
          <Heading level="h2">{t('typography.dashboard.insights')}</Heading>
          <Badge variant="interactive">{t('typography.dashboard.beta')}</Badge>
        </div>
        <Insights.Filters testId="donation-filters" labels={filterLabels} />
      </div>

      <DonationSummaryCharts />
    </Insights.Domain>
  )
}
